/*
  Scroll reveal for `.rv`, ported from _design/adventure-store-v2.html.

  Loaded alongside Dawn's own animations.js and behind the same
  `animations_reveal_on_scroll` setting, so the theme has a single toggle:
  Dawn drives `.scroll-trigger` on its stock sections, this drives `.rv` on the
  ported ones.

  `.rv` hides its element, so the failure modes are handled deliberately:
  - the hidden state only applies while `.nld-reveal` is on <html>, and the
    inline stamp in theme.liquid removes that class again if this file never
    reports in, so a blocked or failed script leaves the content visible;
  - the mock's `threshold: 0.12` is expressed as a bottom rootMargin instead,
    because an element taller than about eight viewports can never put 12% of
    itself on screen and would sit hidden forever;
  - anything injected after load (collection filtering, search, the quick add
    modal) is picked up by a MutationObserver, since a `.rv` that nothing ever
    observes is a `.rv` that never appears.
*/
(() => {
  const STAGGER_MS = 70;
  const STAGGER_GROUP = 4;

  const root = document.documentElement;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const observed = new WeakSet();

  /* The inline stamp's fallback already fired - this file took more than three
     seconds to arrive - so every `.rv` element is on screen unhidden. Revealing
     what is already revealed would only flash it. */
  if (!root.classList.contains('nld-reveal')) return;

  /* Tells the inline stamp in theme.liquid that hiding is safe: the script it
     was waiting for is running. */
  root.dataset.nldReveal = 'ready';

  /* A revealed element is done with. Dropping the classes hands its transform
     and transition back to the section's own CSS, which matters for anything
     that writes its own transform afterwards. */
  const settle = (element) => {
    element.classList.remove('rv', 'in');
    element.style.transitionDelay = '';
  };

  const show = (element) => {
    element.classList.add('in');

    const onEnd = (event) => {
      /* transitionend bubbles, so a child running its own transition - a
         button hover mid-reveal, say - must not be read as the reveal
         finishing. */
      if (event.target !== element) return;
      element.removeEventListener('transitionend', onEnd);
      settle(element);
    };

    element.addEventListener('transitionend', onEnd);
  };

  const observer =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              observer.unobserve(entry.target);
              show(entry.target);
            });
          },
          { rootMargin: '0px 0px -12% 0px' },
        )
      : null;

  const scan = (within) => {
    const scope = within && within.querySelectorAll ? within : document;
    const elements = scope.querySelectorAll('.rv:not(.in)');

    /* In the editor a section is re-rendered under the merchant's cursor, so
       it has to be visible straight away rather than waiting for a scroll.
       Dawn short-circuits its own reveal the same way. */
    const immediate = !observer || reduce.matches || window.Shopify?.designMode;

    elements.forEach((element, index) => {
      if (observed.has(element)) return;
      observed.add(element);

      /* No transition to wait on here, so the classes come off directly
         rather than through a transitionend that would never arrive. */
      if (immediate) {
        settle(element);
        return;
      }

      const delay = (index % STAGGER_GROUP) * STAGGER_MS;
      if (delay) element.style.transitionDelay = `${delay}ms`;
      observer.observe(element);
    });
  };

  scan();

  document.addEventListener('shopify:section:load', (event) => scan(event.target));

  reduce.addEventListener('change', () => {
    if (reduce.matches) document.querySelectorAll('.rv').forEach(settle);
  });

  if ('MutationObserver' in window) {
    let queued = false;

    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        scan();
      });
    }).observe(document.body, { childList: true, subtree: true });
  }
})();
